import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { usePage } from '@/hooks/useIngestion'
import { EntityGraphView } from './EntityGraphView'
import type { Chunk, Entity } from '@/types/ingestion'

const TYPE_COLOURS = [
  '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b',
  '#14b8a6', '#ef4444', '#ec4899', '#6366f1',
]

function hashString(s: string): number {
  let hash = 0
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

function getTypeColour(entityType: string): string {
  return TYPE_COLOURS[hashString(entityType) % TYPE_COLOURS.length]
}

interface PageDetailDrawerProps {
  runId: number | string
  pageId: number | null
  onClose: () => void
}

export function PageDetailDrawer({ runId, pageId, onClose }: PageDetailDrawerProps) {
  const { data: page, isLoading } = usePage(runId, pageId ?? 0)

  return (
    <Sheet open={pageId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[680px] max-w-[680px] overflow-y-auto sm:max-w-[680px]">
        <SheetHeader>
          <SheetTitle className="truncate">
            {isLoading ? <Skeleton className="h-5 w-48" /> : (page ? new URL(page.url).pathname : '')}
          </SheetTitle>
          <SheetDescription>
            {isLoading ? <Skeleton className="h-4 w-64" /> : page?.url}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <DrawerSkeleton />
        ) : page ? (
          <Tabs defaultValue="graph" className="mt-4">
            <TabsList>
              <TabsTrigger value="graph">Graph</TabsTrigger>
              <TabsTrigger value="chunks">Chunks</TabsTrigger>
              <TabsTrigger value="entities">Entities</TabsTrigger>
              <TabsTrigger value="raw">Raw</TabsTrigger>
            </TabsList>

            <TabsContent value="graph" className="mt-3">
              <EntityGraphView
                entities={page.entities ?? []}
                triplets={page.triplets ?? []}
              />
            </TabsContent>

            <TabsContent value="chunks" className="mt-3">
              <ChunksTab chunks={page.chunks ?? []} />
            </TabsContent>

            <TabsContent value="entities" className="mt-3">
              <EntitiesTab entities={page.entities ?? []} />
            </TabsContent>

            <TabsContent value="raw" className="mt-3">
              <RawTab markdown={page.raw_markdown ?? ''} />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="py-12 text-center text-muted-foreground text-sm">
            Page not found
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

function DrawerSkeleton() {
  return (
    <div className="mt-4 space-y-4">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-[240px] w-full rounded-lg" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )
}

function ChunksTab({ chunks }: { chunks: Chunk[] }) {
  if (chunks.length === 0) {
    return <div className="py-12 text-center text-muted-foreground text-sm">No chunks available</div>
  }

  return (
    <div className="space-y-3">
      {chunks.map((chunk) => (
        <ChunkCard key={chunk.id} chunk={chunk} totalChunks={chunks.length} />
      ))}
    </div>
  )
}

function ChunkCard({ chunk, totalChunks }: { chunk: Chunk; totalChunks: number }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = chunk.text.length > 300

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-xs">
          {chunk.chunk_index + 1} / {totalChunks}
        </Badge>
        <span className="text-xs text-muted-foreground">{chunk.token_count} tokens</span>
      </div>

      <p className="text-sm leading-relaxed">
        {expanded || !isLong ? chunk.text : chunk.text.slice(0, 300) + '…'}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-primary hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}

      {chunk.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chunk.tags.map((tag, i) => (
            <Badge key={i} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {chunk.questions.length > 0 && (
        <div className="space-y-1">
          {chunk.questions.map((q, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                Q
              </span>
              <span className="text-muted-foreground">{q}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function EntitiesTab({ entities }: { entities: Entity[] }) {
  if (entities.length === 0) {
    return <div className="py-12 text-center text-muted-foreground text-sm">No entities available</div>
  }

  const mergedCount = entities.filter((e) => e.aliases && e.aliases.length > 0).length

  return (
    <div className="space-y-2">
      {mergedCount > 0 && (
        <p className="text-xs text-muted-foreground">{mergedCount} merged</p>
      )}
      {entities.map((entity) => (
        <div key={entity.id} className="flex items-start justify-between rounded-lg border p-3">
          <div className="space-y-1">
            <span className="font-medium text-sm">{entity.name}</span>
            {entity.aliases && entity.aliases.length > 0 && (
              <p className="text-xs text-muted-foreground">
                aka {entity.aliases.join(', ')}
              </p>
            )}
          </div>
          <Badge
            variant="outline"
            className="shrink-0 text-xs"
            style={{
              borderColor: getTypeColour(entity.entity_type),
              color: getTypeColour(entity.entity_type),
            }}
          >
            {entity.entity_type}
          </Badge>
        </div>
      ))}
    </div>
  )
}

function RawTab({ markdown }: { markdown: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // fallback — ignore
    }
  }

  if (!markdown) {
    return <div className="py-12 text-center text-muted-foreground text-sm">No raw content available</div>
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-7 w-7"
        onClick={handleCopy}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
      <pre className="rounded-lg bg-muted p-4 pr-12 text-xs leading-relaxed overflow-auto max-h-[60vh] whitespace-pre-wrap break-words font-mono">
        {markdown}
      </pre>
    </div>
  )
}
